import { useEffect } from "react";
import {
  ArrowRight,
  Bot,
  Inbox,
  LayoutDashboard,
  LogIn,
  MessagesSquare,
  Send,
  Settings2,
  ShieldCheck,
  Users,
  Workflow,
} from "lucide-react";
import LoginPanel from "./login-panel";
import ThemeToggle from "./theme-toggle";
import LangToggle from "./lang-toggle";
import { useThemeMode } from "./theme-mode";
import { useAuth } from "./auth";
import { fontsHref, themeStyle } from "./theme";
import site from "./site";
import { t, tn } from "@/lib/i18n";

const theme = (site as { theme?: { accent?: string; mode?: string; style?: string } }).theme || {};
const accent = theme.accent || "teal";
const style = theme.style || "modern";
const brand = (site as { name?: string }).name || "Hub";

const previewRows = [
  { name: t("Мария Соколова"), channel: "WhatsApp", text: t("Здравствуйте! Нужен счёт на 12 позиций"), time: t("2 мин"), unread: 2 },
  { name: t("Игорь Дементьев"), channel: "Telegram", text: t("Когда сможете отгрузить?"), time: t("14 мин"), unread: 0 },
  { name: t("Ольга Смирнова"), channel: "WhatsApp", text: t("Спасибо, всё получили!"), time: t("1 ч"), unread: 0 },
  { name: t("Артём Лебедев"), channel: "Telegram", text: t("Можно звонок на завтра?"), time: t("3 ч"), unread: 1 },
];

const features = [
  { icon: Inbox, title: t("Единые входящие"), text: t("Диалоги WhatsApp и Telegram в одной ленте.") },
  { icon: Users, title: t("Роли и доступ"), text: t("Продавец, руководитель, администратор.") },
  { icon: Bot, title: t("AI-агенты"), text: t("Отвечают круглосуточно и передают людям.") },
  { icon: Send, title: t("Рассылки"), text: t("Одно сообщение сразу большой базе клиентов.") },
  { icon: Workflow, title: t("Рабочие процессы"), text: t("Триггеры, назначения и задания без рутины.") },
  { icon: LayoutDashboard, title: t("Отчёты и дашборд"), text: t("Нагрузка команды и время ответа.") },
];

export default function Landing() {
  const { signInWithGoogle } = useAuth();
  const { mode } = useThemeMode();
  // Подтягиваем те же шрифты, что и в приложении, чтобы главная не выглядела чужой.
  useEffect(() => {
    const href = fontsHref(style);
    if (document.head.querySelector(`link[data-flash-fonts][href="${href}"]`)) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = href;
    l.setAttribute("data-flash-fonts", "");
    document.head.appendChild(l);
  }, []);

  return (
    <div className="flash-site flex min-h-screen w-full flex-col bg-background text-foreground" data-style={style} style={themeStyle(accent, mode, style)}>
      <header className="sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-4 px-5 md:px-8">
          <a href="#top" className="flex items-center gap-3 text-lg font-bold tracking-tight">
            <span className="grid size-9 place-items-center rounded-btn bg-primary text-base font-bold text-primary-foreground" aria-hidden>
              {brand.slice(0, 1).toUpperCase()}
            </span>
            <span className="font-display">{brand}</span>
          </a>
          <nav className="hidden items-center gap-1 md:flex">
            <a href="#features" className="rounded-btn px-3.5 py-2 text-[15px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground">
              {t("Возможности")}
            </a>
            <a href="#roles" className="rounded-btn px-3.5 py-2 text-[15px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground">
              {t("Роли")}
            </a>
            <a href="#access" className="rounded-btn px-3.5 py-2 text-[15px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground">
              {t("Доступ")}
            </a>
          </nav>
          <div className="flex items-center gap-2">
            <LangToggle />
            <ThemeToggle />
            <a
              href="#access"
              className="flash-btn hidden h-10 items-center gap-2 whitespace-nowrap rounded-btn border border-border px-4 text-sm font-semibold transition hover:border-primary/60 hover:text-primary sm:inline-flex"
            >
              <LogIn className="size-4" aria-hidden />
              {t("Вход")}
            </a>
            <button
              type="button"
              onClick={signInWithGoogle}
              className="flash-btn inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              <i className="ri-google-fill text-base leading-none" aria-hidden />
              {t("Войти через Google")}
            </button>
          </div>
        </div>
      </header>

      <main id="top" className="flex-1">
        <section className="relative isolate overflow-hidden border-b border-border">
          <div className="absolute -end-32 -top-40 -z-10 size-[520px] rounded-full bg-primary/15 blur-3xl" aria-hidden />
          <div className="absolute -start-40 top-40 -z-10 size-[420px] rounded-full bg-primary/[.07] blur-3xl" aria-hidden />
          <div className="mx-auto grid w-full max-w-6xl items-center gap-12 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-[1.05fr_.95fr] lg:gap-16">
            <div className="flex flex-col items-start gap-6">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[.14em] text-primary">
                <MessagesSquare className="size-3.5" aria-hidden />
                {t("Единый центр переписки")}
              </span>
              <h1 className="max-w-xl text-4xl leading-[1.06] md:text-5xl lg:text-[3.4rem]">
                {t("Все диалоги клиентов —")} <span className="text-primary">{t("в одном окне")}</span>
              </h1>
              <p className="max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
                {t("{brand} собирает WhatsApp, Telegram и почту в общую ленту для отдела продаж: входящие, контакты, роли, AI-агенты, рассылки и отчёты. Войдите рабочей Google-почтой — и рабочий экран откроется сразу.", { brand })}
              </p>

              <div className="w-full max-w-md">
                <LoginPanel align="start" />
              </div>

              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <ShieldCheck className="size-4 text-primary" aria-hidden />
                {t("Доступ по Google-аккаунту — только для добавленных почт")}
              </p>

              <dl className="mt-2 grid w-full max-w-md grid-cols-3 gap-4 border-t border-border pt-5">
                {[
                  { v: "2", l: t("канала") },
                  { v: "34", l: t("экрана") },
                  { v: "1", l: t("вход") },
                ].map((s) => (
                  <div key={s.l}>
                    <dd className="font-display text-2xl text-primary tabular-nums">{s.v}</dd>
                    <dt className="text-xs text-muted-foreground">{s.l}</dt>
                  </div>
                ))}
              </dl>
            </div>

            <div className="relative">
              <div className="absolute inset-x-8 -bottom-6 -z-10 h-24 rounded-full bg-primary/25 blur-3xl" aria-hidden />
              <div className="overflow-hidden rounded-card border border-border bg-card/80 backdrop-blur">
                <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                  <span className="size-2.5 rounded-full bg-foreground/15" aria-hidden />
                  <span className="size-2.5 rounded-full bg-foreground/15" aria-hidden />
                  <span className="size-2.5 rounded-full bg-foreground/15" aria-hidden />
                  <span className="ms-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Inbox className="size-3.5" aria-hidden />
                    {t("Входящие")}
                  </span>
                </div>
                <div className="flex gap-2 border-b border-border px-4 py-2.5">
                  {[t("Все"), t("Мои"), t("Без ответа")].map((tab, i) => (
                    <span
                      key={tab}
                      className={
                        i === 0
                          ? "rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
                          : "rounded-full px-3 py-1 text-xs font-medium text-muted-foreground"
                      }
                    >
                      {tab}
                    </span>
                  ))}
                </div>
                <ul className="divide-y divide-border">
                  {previewRows.map((r) => (
                    <li key={r.name} className="flex items-start gap-3 px-4 py-3">
                      <span className="grid size-9 flex-none place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                        {r.name
                          .split(" ")
                          .map((p) => p[0])
                          .join("")
                          .slice(0, 2)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-semibold">{r.name}</span>
                          <span className="flex-none text-[11px] text-muted-foreground">{r.time}</span>
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-muted-foreground">{r.text}</span>
                        <span className="mt-1.5 flex items-center gap-2">
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{r.channel}</span>
                          {r.unread > 0 ? (
                            <span className="grid size-4 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">{r.unread}</span>
                          ) : null}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3">
                  <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="size-1.5 rounded-full bg-primary" aria-hidden />
                    {t("{n} без ответа", { n: tn(3, "диалог", "диалога", "диалогов") })}
                  </span>
                  <span className="rounded-btn bg-primary/15 px-2.5 py-1 text-[11px] font-semibold text-primary">{t("Среднее время 4 мин")}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="border-b border-border bg-muted/30 py-12 md:py-16">
          <div className="mx-auto w-full max-w-6xl px-5 md:px-8">
            <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-2xl md:text-3xl">{t("Что внутри")}</h2>
                <p className="mt-2 max-w-xl text-sm text-muted-foreground md:text-base">
                  {t("После входа открывается рабочий экран, а меню слева внизу переключает между всеми разделами приложения.")}
                </p>
              </div>
              <a href="#access" className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-primary hover:underline">
                {t("Войти")}
                <ArrowRight className="size-4" aria-hidden />
              </a>
            </div>
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((f) => (
                <li key={f.title} className="flex items-start gap-4 rounded-card border border-border bg-card/70 p-4 md:p-5">
                  <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                    <f.icon className="size-5" />
                  </span>
                  <span>
                    <b className="block text-sm font-semibold md:text-base">{f.title}</b>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground md:text-sm">{f.text}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="roles" className="border-b border-border py-12 md:py-16">
          <div className="mx-auto grid w-full max-w-6xl gap-8 px-5 md:px-8 lg:grid-cols-[.9fr_1.1fr] lg:gap-14">
            <div>
              <h2 className="text-2xl md:text-3xl">{t("Каждый видит своё")}</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground md:text-base">
                {t("Доступ к разделам зависит от роли. Администратор подключает каналы и приглашает коллег, руководитель видит все диалоги и отчёты, продавец работает только со своими клиентами.")}
              </p>
            </div>
            <ul className="flex flex-col gap-3">
              {[
                { t: t("Владелец / администратор"), d: t("Все разделы, приглашения, каналы, настройки и биллинг"), b: t("Полный доступ") },
                { t: t("Руководитель"), d: t("Все диалоги, отчёты, переназначение и рабочие процессы"), b: t("Без настроек") },
                { t: t("Продавец"), d: t("Только свои диалоги, контакты и шаблоны ответов"), b: t("Свои клиенты") },
              ].map((r) => (
                <li key={r.t} className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-border bg-card/70 px-4 py-4 md:px-5">
                  <span className="min-w-0">
                    <b className="block text-sm font-semibold md:text-base">{r.t}</b>
                    <span className="mt-1 block text-xs text-muted-foreground md:text-sm">{r.d}</span>
                  </span>
                  <span className="whitespace-nowrap rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-[11px] font-semibold text-primary">{r.b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="access" className="py-14 md:py-20">
          <div className="mx-auto w-full max-w-6xl px-5 md:px-8">
            <div className="flex flex-col items-center gap-6 rounded-card border border-border bg-card/70 px-6 py-10 text-center md:px-12 md:py-14">
              <span className="inline-flex items-center gap-2 rounded-full border border-border px-3.5 py-1.5 text-xs font-semibold text-muted-foreground">
                <Settings2 className="size-3.5" aria-hidden />
                {t("Вход в рабочее пространство")}
              </span>
              <h2 className="max-w-2xl text-2xl leading-tight md:text-4xl">{t("Войдите рабочей Google-почтой")}</h2>
              <p className="max-w-xl text-sm text-muted-foreground md:text-base">
                {t("Аккаунт Google подтверждает личность, а доступ к разделам выдаётся по вашей роли в команде. Пускаем только почты, которые владелец или администратор добавил в «Настройках пользователей».")}
              </p>
              <LoginPanel align="center" />
              <p className="text-xs text-muted-foreground">{t("Нет доступа? Попросите администратора вашей команды добавить почту.")}</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border py-6">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-3 px-5 text-xs text-muted-foreground sm:flex-row md:px-8">
          <span>© {new Date().getFullYear()} {brand} — {t("единый центр переписки")}</span>
          <span className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-primary" aria-hidden />
            {t("Данные хранятся в вашем рабочем пространстве")}
          </span>
        </div>
      </footer>
    </div>
  );
}